/**
 * The conversation's cache (`KLQ-20260820-001` phase 1).
 *
 * **Nothing polls and nothing subscribes.** A drawing's conversation changes when somebody on
 * this screen changes it, or when somebody elsewhere does — and the answer to the second case
 * is a refetch on window focus, not a socket. `CLAUDE.md`: *"Ask 'does this actually need to
 * be live?' before adding any websocket subscription. Default is no."*
 *
 * **The feed is paged by cursor, so it is an infinite query.** `getNextPageParam` reads
 * `next_cursor`, which the server sends only when a further page exists — so the hook needs no
 * rule of its own about when to stop, and the cursor is opaque and signed on the way back
 * (`core/pagination.py`), which is what keeps a client from ever constructing one.
 *
 * **A search term is part of the key, not a filter over a cached list.** Typing re-queries;
 * it does not re-scan a list in memory. The alternative — fetch everything, filter locally —
 * is exactly the "repeatedly scans large client-side collections" shape the data-fetching rule
 * calls out, and it would also be wrong: the page the server sent is not the whole project.
 */

import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { PROJECTS_QUERY_KEY } from '@/features/projects';

import type { CommentBody } from './api';
import { createComment, listComments, readFeed, resolveComment } from './api';

function conversationKey(projectId: string) {
  return [...PROJECTS_QUERY_KEY, 'conversation', projectId] as const;
}

/**
 * What happened under the current slice.
 *
 * The slice is part of the key because it is part of the question: selecting a floor is a
 * different feed, not a filtered view of the project's — filtering a paginated list client-side
 * cannot fill a page, and the page the server sent is not the whole project. An empty slice
 * means the project.
 *
 * The key holds the ids **sorted and joined**, so selecting A then B and selecting B then A are
 * one cache entry rather than two identical requests.
 */
export function feedQueryOptions(
  projectId: string,
  under: readonly string[],
  search: string,
) {
  const slice = [...under].sort().join(',');
  return infiniteQueryOptions({
    queryKey: [...conversationKey(projectId), 'feed', slice === '' ? 'project' : slice, search] as const,
    queryFn: ({ pageParam }) =>
      readFeed(projectId, {
        ...(under.length === 0 ? {} : { under }),
        ...(search === '' ? {} : { q: search }),
        ...(pageParam === null ? {} : { cursor: pageParam }),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? null,
    staleTime: 30_000,
  });
}

export function commentsQueryOptions(projectId: string, slotId: string) {
  return queryOptions({
    queryKey: [...conversationKey(projectId), 'thread', slotId] as const,
    queryFn: () => listComments(projectId, slotId),
    staleTime: 30_000,
  });
}

export function useFeed(projectId: string, under: readonly string[], search: string) {
  return useInfiniteQuery(feedQueryOptions(projectId, under, search));
}

export function useComments(projectId: string, slotId: string | null) {
  return useQuery({
    ...commentsQueryOptions(projectId, slotId ?? 'none'),
    enabled: slotId !== null,
  });
}

/**
 * What a write to the conversation invalidates — **the thread now, the feeds later.**
 *
 * This used to sweep `conversationKey(projectId)` whole: one comment refetched the thread *and*
 * every cached feed — and a feed is an infinite query, which TanStack refetches page by page,
 * so a person ten pages into the project feed paid ten requests for one reply (review
 * 2026-09-25 §3.23). The coarseness had a reason worth keeping: a new comment changes every
 * feed scope that contains the drawing — the project's, its floor's, its building's — and the
 * client cannot know which without walking the tree.
 *
 * So the rule is split by what is on screen. **The thread is refetched, exactly**: it is the
 * surface the person is looking at when they write, and a stale thread under a fresh comment
 * is the one thing this must never show. **Every feed is marked stale and left alone**
 * (`refetchType: 'none'`): no screen mounts a thread and a feed at once — `CommentThread` sits
 * in the viewer's rail and in `SlotDetail`, `FeedPanel` on the overview and in
 * `ContainerDetail`, and the drawings screen shows one detail or the other — so a feed is
 * always refetched on its next mount, which is the next time anybody can see it. Coarse across
 * feeds, still: which scopes contain the drawing stays the server's question.
 *
 * `slotId` is `null` only for a caller that has no idea which drawing changed; every thread
 * is then marked, and the mounted one refetches, which is the old behaviour for threads alone.
 */
function invalidateConversation(
  projectId: string,
  queryClient: ReturnType<typeof useQueryClient>,
  slotId: string | null,
): void {
  if (slotId === null) {
    void queryClient.invalidateQueries({ queryKey: [...conversationKey(projectId), 'thread'] });
  } else {
    void queryClient.invalidateQueries({
      queryKey: commentsQueryOptions(projectId, slotId).queryKey,
      exact: true,
    });
  }
  void queryClient.invalidateQueries({
    queryKey: [...conversationKey(projectId), 'feed'],
    refetchType: 'none',
  });
}

/**
 * The same invalidation, for a write this feature does not own.
 *
 * The viewer creates a pin *and its first comment* in one request, which changes this
 * feature's thread and every feed scope containing the drawing — and it is `features/comments`
 * that knows what those keys are. Handing out the invalidation rather than the key is retro
 * rule 8 in its smallest form: one owner of the rule, one call site per consumer, and no
 * second place that has to be edited the day the key gains a segment. The caller names the
 * drawing when it knows it, so the thread refetch can be exact.
 */
export function useInvalidateConversation(
  projectId: string,
): (slotId?: string | null) => void {
  const queryClient = useQueryClient();
  return (slotId = null) => {
    invalidateConversation(projectId, queryClient, slotId);
  };
}

export function useCreateComment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CommentBody) => createComment(projectId, body),
    retry: false,
    onSuccess: (comment) => {
      invalidateConversation(projectId, queryClient, comment.drawing_slot_id);
    },
  });
}

export function useResolveComment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => resolveComment(commentId),
    retry: false,
    onSuccess: (comment) => {
      invalidateConversation(projectId, queryClient, comment.drawing_slot_id);
    },
  });
}
